/**
 * Hero Video — early preload, sound on play, hide poster when playback starts.
 */
class HeroVideoComponent extends HTMLElement {
  constructor() {
    super();
    this.isPlaying = false;
    this.embedInjected = false;
    this.onPlayClick = this.onPlayClick.bind(this);
    this.onWarm = this.onWarm.bind(this);
  }

  connectedCallback() {
    this.section = this.closest('.bst-hero-video');
    this.playButton = this.querySelector('[data-hero-video-play]');
    this.poster = this.querySelector('[data-hero-video-poster]');
    this.video = this.querySelector('[data-hero-video-element]');
    this.embedTemplate = this.querySelector('[data-hero-video-embed-template]');

    if (!this.playButton) return;

    this.playButton.addEventListener('click', this.onPlayClick);
    this.playButton.addEventListener('mouseenter', this.onWarm);
    this.playButton.addEventListener('focus', this.onWarm);

    if (this.video) {
      this.configureHostedVideo();
      this.onWarm();
      this.observePreload();
    } else if (this.embedTemplate) {
      this.onWarm();
      this.observePreload();
    }
  }

  disconnectedCallback() {
    this.disconnectObserver?.();
    this.playButton?.removeEventListener('click', this.onPlayClick);
    this.playButton?.removeEventListener('mouseenter', this.onWarm);
    this.playButton?.removeEventListener('focus', this.onWarm);
  }

  shouldLoop() {
    return this.dataset.videoLoop !== 'false';
  }

  configureHostedVideo() {
    this.video.controls = false;
    this.video.loop = this.shouldLoop();
    this.video.playsInline = true;
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('webkit-playsinline', '');
    this.video.removeAttribute('poster');
    this.video.muted = true;
    this.video.preload = 'metadata';
  }

  observePreload() {
    if (!('IntersectionObserver' in window)) return;

    this.disconnectObserver = () => {
      if (this.preloadObserver) {
        this.preloadObserver.disconnect();
        this.preloadObserver = null;
      }
    };

    this.preloadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.onWarm();
        }
      },
      { rootMargin: '400px 0px', threshold: 0 }
    );

    this.preloadObserver.observe(this);
  }

  onWarm() {
    if (this.video) {
      this.video.preload = 'auto';
      if (this.video.readyState < 2) {
        this.video.load();
      }
      return;
    }

    if (this.embedTemplate && !this.embedInjected) {
      this.preconnectEmbed();
    }
  }

  preconnectEmbed() {
    const iframe = this.embedTemplate.content.querySelector('iframe');
    if (!iframe) return;

    const src = iframe.getAttribute('src') || '';
    let origin = '';

    if (src.includes('youtube.com') || src.includes('youtu.be')) {
      origin = 'https://www.youtube.com';
    } else if (src.includes('vimeo.com')) {
      origin = 'https://player.vimeo.com';
    }

    if (!origin || document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;

    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  onPlayClick() {
    if (this.isPlaying) return;

    this.section?.classList.add('bst-hero-video--loading');
    this.setAttribute('data-loading', '');

    if (this.video) {
      this.startHostedVideo();
      return;
    }

    if (this.embedTemplate) {
      this.startEmbedVideo();
    }
  }

  startHostedVideo() {
    this.video.preload = 'auto';
    this.video.loop = this.shouldLoop();
    this.video.controls = false;
    this.video.muted = false;
    this.video.volume = 1;

    const onPlaying = () => {
      this.video.removeEventListener('playing', onPlaying);
      this.setPlayingState();
    };

    this.video.addEventListener('playing', onPlaying);

    const tryPlay = () => {
      const attempt = this.video.play();

      if (!attempt || typeof attempt.then !== 'function') {
        return;
      }

      attempt.catch(() => {
        this.video.muted = true;
        return this.video.play();
      }).catch(() => {
        this.video.removeEventListener('playing', onPlaying);
        this.section?.classList.remove('bst-hero-video--loading');
        this.removeAttribute('data-loading');
      });
    };

    if (this.video.readyState >= 3) {
      tryPlay();
      return;
    }

    if (this.video.readyState < 2) {
      this.video.load();
    }

    const onCanPlay = () => {
      this.video.removeEventListener('canplay', onCanPlay);
      tryPlay();
    };

    this.video.addEventListener('canplay', onCanPlay);

    window.setTimeout(() => {
      if (!this.isPlaying && this.video.readyState >= 2) {
        tryPlay();
      }
    }, 100);
  }

  startEmbedVideo() {
    if (!this.embedInjected) {
      const node = this.embedTemplate.content.firstElementChild.cloneNode(true);
      this.appendChild(node);
      this.embedInjected = true;
    }

    this.setPlayingState();
  }

  setPlayingState() {
    this.isPlaying = true;
    this.setAttribute('data-playing', '');
    this.section?.classList.add('bst-hero-video--playing');
    this.section?.classList.remove('bst-hero-video--loading');
    this.removeAttribute('data-loading');
  }
}

customElements.define('hero-video-component', HeroVideoComponent);
